/**
 * Hook para gerenciar preferências do dashboard
 * 
 * Gerencia preferências do usuário para o dashboard, incluindo:
 * - Filtro de período
 * - KPIs selecionados para exibição
 * - Outras configurações personalizadas
 * 
 * As preferências são salvas no localStorage e persistem entre sessões.
 * 
 * @module hooks/useDashboardPreferences
 */

import { useState, useEffect, useCallback } from 'react';
import kpiConfig from '../utils/dashboard/kpiConfig';

/**
 * Chave para armazenar preferências no localStorage
 */
const STORAGE_KEY = 'dashboard_preferences';

/**
 * Preferências padrão
 */
const DEFAULT_PREFERENCES = {
    // Período padrão: '30' (últimos 30 dias)
    period: '30',
    // KPIs visíveis: todos os KPIs por padrão
    visibleKPIs: kpiConfig.map(kpi => kpi.key),
    // Tempo real: desabilitado por padrão
    realtimeEnabled: false,
    // Intervalo de atualização em tempo real (ms)
    realtimeInterval: 30000, // 30 segundos
    // Outras preferências futuras podem ser adicionadas aqui
};

/**
 * Carrega preferências do localStorage
 * 
 * @returns {object} Preferências carregadas ou padrão
 */
const loadPreferences = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Lista de todos os KPIs disponíveis atualmente
            const allKpiKeys = kpiConfig.map(kpi => kpi.key);
            
            // Garante que todas as chaves necessárias existam
            let visibleKPIs = Array.isArray(parsed.visibleKPIs) 
                ? parsed.visibleKPIs 
                : DEFAULT_PREFERENCES.visibleKPIs;
            
            // Adiciona novos KPIs que não estavam nas preferências salvas
            // Isso garante que novos KPIs adicionados ao sistema apareçam automaticamente
            const newKPIs = allKpiKeys.filter(key => !visibleKPIs.includes(key));
            if (newKPIs.length > 0) {
                visibleKPIs = [...visibleKPIs, ...newKPIs];
            }
            
            return {
                ...DEFAULT_PREFERENCES,
                ...parsed,
                visibleKPIs,
            };
        }
    } catch (error) {
        console.error('Erro ao carregar preferências do dashboard:', error);
    }
    return DEFAULT_PREFERENCES;
};

/**
 * Salva preferências no localStorage
 * 
 * @param {object} preferences - Preferências a serem salvas
 */
const savePreferences = (preferences) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
        console.error('Erro ao salvar preferências do dashboard:', error);
    }
};

/**
 * Hook para gerenciar preferências do dashboard
 * 
 * @returns {object} Objeto com preferências e funções para atualizá-las
 * @property {object} preferences - Preferências atuais
 * @property {string} preferences.period - Período selecionado ('7', '30', '90', '365', 'custom')
 * @property {Array<string>} preferences.visibleKPIs - Array de chaves de KPIs visíveis
 * @property {function} setPeriod - Função para atualizar o período
 * @property {function} toggleKPI - Função para alternar visibilidade de um KPI
 * @property {function} setVisibleKPIs - Função para definir KPIs visíveis
 * @property {function} resetPreferences - Função para resetar preferências para padrão
 * @property {function} updatePreferences - Função para atualizar múltiplas preferências
 * 
 * @example
 * ```javascript
 * const { preferences, setPeriod, toggleKPI, resetPreferences } = useDashboardPreferences();
 * 
 * // Atualizar período
 * setPeriod('90');
 * 
 * // Alternar visibilidade de um KPI
 * toggleKPI('pacientes_ativos');
 * 
 * // Resetar preferências
 * resetPreferences();
 * ```
 */
export const useDashboardPreferences = () => {
    const [preferences, setPreferencesState] = useState(() => loadPreferences());

    /**
     * Atualiza preferências e salva no localStorage
     */
    const updatePreferences = useCallback((newPreferences) => {
        setPreferencesState((prev) => {
            const updated = { ...prev, ...newPreferences };
            savePreferences(updated);
            return updated;
        });
    }, []);

    /**
     * Atualiza o período selecionado
     * 
     * @param {string} period - Período ('7', '30', '90', '365', 'custom')
     */
    const setPeriod = useCallback((period) => {
        updatePreferences({ period });
    }, [updatePreferences]);

    /**
     * Alterna a visibilidade de um KPI
     * 
     * @param {string} kpiKey - Chave do KPI
     */
    const toggleKPI = useCallback((kpiKey) => {
        setPreferencesState((prev) => {
            const visibleKPIs = prev.visibleKPIs.includes(kpiKey)
                ? prev.visibleKPIs.filter(key => key !== kpiKey)
                : [...prev.visibleKPIs, kpiKey];
            
            const updated = { ...prev, visibleKPIs };
            savePreferences(updated);
            return updated;
        });
    }, []);

    /**
     * Define quais KPIs estão visíveis
     * 
     * @param {Array<string>} kpiKeys - Array de chaves de KPIs
     */
    const setVisibleKPIs = useCallback((kpiKeys) => {
        updatePreferences({ visibleKPIs: kpiKeys });
    }, [updatePreferences]);

    /**
     * Atualiza configuração de tempo real
     * 
     * @param {boolean} enabled - Se tempo real está habilitado
     * @param {number} [interval] - Intervalo de atualização em ms
     */
    const setRealtimeConfig = useCallback((enabled, interval = null) => {
        setPreferencesState((prev) => {
            const updated = {
                ...prev,
                realtimeEnabled: enabled,
            };
            if (interval !== null) {
                updated.realtimeInterval = interval;
            }
            savePreferences(updated);
            return updated;
        });
    }, []);

    /**
     * Reseta preferências para os valores padrão
     */
    const resetPreferences = useCallback(() => {
        setPreferencesState(DEFAULT_PREFERENCES);
        savePreferences(DEFAULT_PREFERENCES);
    }, []);

    /**
     * Sincroniza preferências do localStorage quando a janela recebe foco
     * (útil se o usuário tem múltiplas abas abertas)
     */
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY) {
                setPreferencesState(loadPreferences());
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    return {
        preferences,
        setPeriod,
        toggleKPI,
        setVisibleKPIs,
        setRealtimeConfig,
        resetPreferences,
        updatePreferences,
    };
};

export default useDashboardPreferences;

